import { logger } from '../services/logger.ts';
import React, { useState } from 'react';
import { X, CreditCard, AlertCircle, ExternalLink } from 'lucide-react';
import { useAuth } from '../lib/auth.tsx';
import { createPeachCheckout } from '../lib/api.ts';
import { isPeachPaymentConfigured } from '../lib/peachPaymentConfig.ts';

interface PeachPaymentFormProps {
  amount: number;
  walletCreditAmount?: number; // Amount to actually credit to wallet (excluding fees)
  onSuccess: (transactionId: string) => void;
  onError: (error: string) => void;
  onCancel: () => void;
}

export function PeachPaymentForm({ amount, walletCreditAmount, onSuccess: _onSuccess, onError, onCancel }: PeachPaymentFormProps) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isConfigured] = useState(isPeachPaymentConfigured());
  
  const { user } = useAuth();

  const handlePaymentRedirect = async () => {
    setLoading(true);
    setErrorMessage(null);
    
    try {
      const baseUrl = globalThis.location?.origin || '';
      const returnUrl = `${baseUrl}/wallet?payment_result=true`;

      const creditAmount = walletCreditAmount || amount;
      const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      const amountStr = Math.floor(creditAmount).toString().padStart(3, '0');
      const merchantTxId = `hosted_${randomNum}_${amountStr}`;

      logger.info('Creating hosted checkout session...', {
        amount,
        creditAmount,
        merchantTxId,
        returnUrl
      });

      const checkoutResponse = await createPeachCheckout(
        amount,
        returnUrl,
        {
          currency: 'ZAR', // Assuming ZAR as default currency
          merchantTransactionId: merchantTxId,
          customerId: user?.id,
        }
      );

      if (checkoutResponse.success && checkoutResponse.redirectUrl) {
        logger.info('Checkout session created, redirecting to hosted page...', {
          checkoutId: checkoutResponse.checkoutId,
          redirectUrl: checkoutResponse.redirectUrl
        });

        // Redirect to Peach Payments hosted checkout page
        globalThis.location.href = checkoutResponse.redirectUrl;
        
        // Note: After payment, Peach will redirect back to returnUrl
        // The actual success handling will be done via the return URL in WalletManagement.tsx
        
      } else {
        throw new Error(checkoutResponse.error || 'Failed to create checkout session');
      }
    } catch (error) {
      logger.error('Error creating hosted checkout:', error);
      setLoading(false);
      const errorMsg = error instanceof Error ? error.message : 'Failed to initialize payment';
      setErrorMessage(errorMsg);
      onError(errorMsg);
    }
  };

  if (!isConfigured) {
    return (
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-auto shadow-xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold flex items-center gap-2 text-gray-800">
            <div className="bg-red-500 text-white p-2 rounded-lg">
              <AlertCircle className="w-5 h-5" />
            </div>
            Configuration Error
          </h3>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          <p className="font-semibold mb-2">Peach Payments is not configured.</p>
          <p className="text-sm">
            Please ensure the Peach Checkout environment variables are properly set.
          </p>
        </div>
        
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-6 max-w-md w-full mx-auto shadow-xl">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold flex items-center gap-2 text-gray-800">
          <div className="bg-wayja-teal text-white p-2 rounded-lg">
            <CreditCard className="w-5 h-5" />
          </div>
          Secure Payment
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {errorMessage && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="mb-6">
        <div className="bg-blue-50 border border-blue-200 text-blue-700 rounded-lg p-4 mb-4">
          <div className="flex items-center mb-2">
            <ExternalLink className="w-5 h-5 mr-2" />
            <span className="font-semibold">Secure Hosted Payment</span>
          </div>
          <p className="text-sm">
            You'll be redirected to Peach Payments' secure checkout page to complete your payment.
            After payment, you'll be automatically returned to your wallet.
          </p>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-center text-sm text-gray-600 mb-2">
            <span>Payment Amount:</span>
            <span className="font-semibold">R{amount.toFixed(2)}</span>
          </div>
          {walletCreditAmount && walletCreditAmount !== amount && (
            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>Wallet Credit:</span>
              <span className="font-semibold text-green-600">R{walletCreditAmount.toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-5 py-2.5 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handlePaymentRedirect}
          disabled={loading}
          className="flex-1 px-6 py-2.5 bg-wayja-teal text-white font-semibold rounded-lg shadow-md hover:bg-wayja-dark-teal focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:ring-opacity-75 transition-colors flex items-center justify-center"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Redirecting...
            </>
          ) : (
            <>
              <ExternalLink className="w-4 h-4 mr-2" />
              Pay Securely
            </>
          )}
        </button>
      </div>

      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>Protected by 256-bit SSL encryption</p>
        <p>Powered by Peach Payments</p>
      </div>
    </div>
  );
}